import { useState, useRef, useEffect, useLayoutEffect, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Sparkles, CheckCircle2, Move, Circle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { CloseButton, Heading, Pressable, Button, IconButton } from '../../../shared/ui';

type GrowStage = 'goal' | 'reality' | 'options' | 'wayforward';

interface StageData {
  title: string;
  /** Stage colours are content of the method, not UI roles — applied through `style`. */
  barColor: string;
  dotColor: string;
  questions: string[];
  icon: string;
}

const stagesData: Record<GrowStage, StageData> = {
  goal: {
    title: 'Цель (Goal)',
    barColor: '#fdf3cc',
    dotColor: 'var(--color-accent)',
    icon: '🎯',
    questions: [
      'Какую именно цель хочет достичь клиент?',
      'Как клиент измерит результат?',
      'Почему эта цель важна именно сейчас?',
      'Что изменится, когда цель будет достигнута?',
    ],
  },
  reality: {
    title: 'Реальность (Reality)',
    barColor: '#fdedee',
    dotColor: 'var(--color-primary)',
    icon: '🔍',
    questions: [
      'Где клиент находится сейчас по шкале от 0 до 10?',
      'Что уже было попробовано?',
      'Какие препятствия есть на пути?',
      'Что работает хорошо в текущей ситуации?',
    ],
  },
  options: {
    title: 'Возможности (Options)',
    barColor: 'var(--color-surface-muted)',
    dotColor: 'var(--color-ink)',
    icon: '💡',
    questions: [
      'Какие есть варианты решения?',
      'Кто мог бы помочь в этой ситуации?',
      'Что еще можно попробовать?',
      'Какие ресурсы доступны клиенту?',
    ],
  },
  wayforward: {
    title: 'Действия (Way Forward)',
    barColor: '#eaf6ea',
    dotColor: '#5ede6b',
    icon: '🚀',
    questions: [
      'Какой первый шаг клиент сделает?',
      'Когда конкретно это произойдет?',
      'Как отследить прогресс?',
      'Что может помешать и как это преодолеть?',
    ],
  },
};

const stageOrder: GrowStage[] = ['goal', 'reality', 'options', 'wayforward'];

/** Все вопросы всех этапов: по самому длинному резервируем высоту блока вопроса, чтобы окно не прыгало при листании */
const allQuestions = stageOrder.flatMap((stage) => stagesData[stage].questions);

export interface GrowPanelProps {
  /** Встраивание в панель инструментов видеокомнаты: без fixed, без перетаскивания */
  embedded?: boolean;
  /** Плавающий режим: закрыть панель целиком (крестик в шапке) */
  onRequestClose?: () => void;
}

export function GrowPanel({ embedded = false, onRequestClose }: GrowPanelProps) {
  const [currentStage, setCurrentStage] = useState<GrowStage>('goal');
  const [completedStages, setCompletedStages] = useState<Set<GrowStage>>(new Set());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // Смещение окна от стандартного места, px (translate): x вправо, y вниз
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const outerRef = useRef<HTMLDivElement>(null);
  // Верх окна перед сворачиванием/разворачиванием: окно привязано к нижнему краю, и без компенсации шапка «прыгает»
  const topBeforeToggleRef = useRef<number | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
    rect: DOMRect;
  } | null>(null);

  const currentData = stagesData[currentStage];
  const currentIndex = stageOrder.indexOf(currentStage);

  const goToNextStage = () => {
    if (currentIndex < stageOrder.length - 1) {
      setCompletedStages((prev) => new Set(prev).add(currentStage));
      setCurrentStage(stageOrder[currentIndex + 1]);
      setCurrentQuestionIndex(0);
    }
  };

  const goToPrevStage = () => {
    if (currentIndex > 0) {
      setCurrentStage(stageOrder[currentIndex - 1]);
      setCurrentQuestionIndex(0);
    }
  };

  const toggleStageComplete = () => {
    setCompletedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(currentStage)) {
        newSet.delete(currentStage);
      } else {
        newSet.add(currentStage);
      }
      return newSet;
    });
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < currentData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Перетаскивание за шапку мышью и пальцем (pointer events + pointer capture, touch-action: none у шапки).
  // Окно не выходит за границы экрана: иначе его можно было утащить за край и потерять.
  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (embedded) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const rect = outerRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setIsDragging(true);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
      rect,
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = Math.min(Math.max(e.clientX - drag.startX, -drag.rect.left), window.innerWidth - drag.rect.right);
    const dy = Math.min(Math.max(e.clientY - drag.startY, -drag.rect.top), window.innerHeight - drag.rect.bottom);

    setPosition({ x: drag.initialX + dx, y: drag.initialY + dy });
  };

  const handlePointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setIsDragging(false);
    dragRef.current = null;
  };

  const toggleCollapsed = () => {
    topBeforeToggleRef.current = outerRef.current?.getBoundingClientRect().top ?? null;
    setCollapsed((value) => !value);
  };

  // Сворачиваем/разворачиваем «на месте»: верх окна (шапка) остаётся там же, где был.
  // Если развёрнутое окно не помещается снизу — поднимаем его ровно настолько, чтобы оно влезло в экран.
  useLayoutEffect(() => {
    const prevTop = topBeforeToggleRef.current;
    topBeforeToggleRef.current = null;
    const el = outerRef.current;
    if (prevTop === null || !el) return;

    const rect = el.getBoundingClientRect();
    const overflow = Math.max(0, prevTop + rect.height - window.innerHeight);
    const desiredTop = Math.max(0, prevTop - overflow);
    const shift = desiredTop - rect.top;
    if (shift !== 0) {
      setPosition((prev) => ({ ...prev, y: prev.y + shift }));
    }
  }, [collapsed]);

  // Поворот экрана, смена размера окна и сворачивание меняют габариты: возвращаем окно в экран
  useEffect(() => {
    if (embedded) return;

    const keepInViewport = () => {
      const rect = outerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const shiftX = Math.max(0, -rect.left) - Math.max(0, rect.right - window.innerWidth);
      const shiftY = Math.max(0, -rect.top) - Math.max(0, rect.bottom - window.innerHeight);
      if (shiftX !== 0 || shiftY !== 0) {
        setPosition((prev) => ({ x: prev.x + shiftX, y: prev.y + shiftY }));
      }
    };

    keepInViewport();
    window.addEventListener('resize', keepInViewport);

    return () => window.removeEventListener('resize', keepInViewport);
  }, [embedded, collapsed, currentStage, currentQuestionIndex]);

  // На телефоне окно по ширине экрана и над панелью управления, на десктопе — справа снизу, как раньше
  const outerClass = embedded
    ? 'h-full min-h-0 flex flex-col relative z-0'
    : 'fixed z-panel pointer-events-auto right-4 bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))] sm:right-8 sm:bottom-8';

  const outerStyle = embedded
    ? undefined
    : {
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'default',
      };

  const panelClass = embedded
    ? 'w-full max-w-full flex-1 min-h-0 flex flex-col bg-ink/55 backdrop-blur-xl rounded-card shadow-raised border border-ink-subtle/45 overflow-hidden'
    : 'w-[480px] max-w-[min(480px,calc(100vw-2rem))] max-h-[calc(var(--vh,1vh)*75)] flex flex-col bg-ink/55 backdrop-blur-xl rounded-card shadow-raised border border-ink-subtle/45 overflow-hidden';

  return (
    <div ref={outerRef} className={outerClass} style={outerStyle}>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ x: embedded ? 0 : 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: embedded ? 0 : 400, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className={panelClass}
        >
          <div
            className={`bg-ink/40 p-2 flex items-center justify-between border-b border-ink-subtle/45 shrink-0 ${
              embedded ? '' : 'cursor-grab active:cursor-grabbing select-none touch-none'
            }`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          >
            <div className="flex items-center gap-2 min-w-0">
              {!embedded && <Move className="w-4 h-4 text-ink-subtle shrink-0" />}
              <Sparkles className="w-4 h-4 text-primary shrink-0" />
              <span className="text-white text-ui-strong truncate">GROW AI Coach</span>
            </div>
            {!embedded && (
              <div className="flex items-center gap-1 shrink-0">
                <IconButton
                  label={collapsed ? 'Развернуть' : 'Свернуть'}
                  variant="ghostInverse"
                  aria-expanded={!collapsed}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapsed();
                  }}
                >
                  {collapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </IconButton>
                {onRequestClose && (
                  <CloseButton
                    size="sm"
                    tone="inverse"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestClose();
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {!(collapsed && !embedded) && (
            <>
              <div className="p-2 border-b border-ink-subtle/45 shrink-0 bg-ink/24">
                <div className="flex gap-2 mb-2">
                  {stageOrder.map((stage) => (
                    <Pressable
                      key={stage}
                      aria-label={`Этап ${stagesData[stage].title}`}
                      aria-current={stage === currentStage ? 'step' : undefined}
                      onClick={() => {
                        setCurrentStage(stage);
                        setCurrentQuestionIndex(0);
                      }}
                      className={`flex-1 h-1.5 rounded-full transition-all ${
                        stage === currentStage ? '' : completedStages.has(stage) ? 'bg-success/50' : 'bg-ink-subtle/50'
                      }`}
                      style={stage === currentStage ? { backgroundColor: stagesData[stage].barColor } : undefined}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-body-xl shrink-0">{currentData.icon}</span>
                    <Heading size="sm" as="h3" tone="inverse" className="truncate">
                      {currentData.title}
                    </Heading>
                  </div>
                  <div className="text-ui-xs text-ink-subtle shrink-0">
                    {currentIndex + 1} / {stageOrder.length}
                  </div>
                </div>
              </div>

              <div className="p-3 flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden min-w-0 bg-ink/16">
                <div className="flex items-center justify-between mb-3 shrink-0">
                  <p className="text-ui-xs text-ink-subtle">
                    Вопрос {currentQuestionIndex + 1} из {currentData.questions.length}
                  </p>
                  <IconButton
                    label="Этап пройден"
                    variant="ghostInverse"
                    aria-pressed={completedStages.has(currentStage)}
                    onClick={toggleStageComplete}
                  >
                    <CheckCircle2
                      className={`w-4 h-4 ${completedStages.has(currentStage) ? 'text-success' : 'text-ink-subtle'}`}
                    />
                  </IconButton>
                </div>

                {/* min-h вместо фиксированной высоты, а высота по самому длинному вопросу: окно не обрезает вопрос и не прыгает */}
                <div className="relative w-full shrink-0 overflow-hidden min-h-[9rem] grid">
                  {allQuestions.map((question, index) => (
                    <div
                      key={`sizer-${index}`}
                      aria-hidden
                      className="col-start-1 row-start-1 invisible p-4 border border-transparent pointer-events-none"
                    >
                      <p
                        className={`leading-relaxed break-words ${
                          embedded
                            ? 'text-ui sm:text-body-l'
                            : 'text-body-xl sm:text-display-s lg:text-display-m font-normal'
                        }`}
                      >
                        {question}
                      </p>
                    </div>
                  ))}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={`${currentStage}-${currentQuestionIndex}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="col-start-1 row-start-1 flex items-center"
                    >
                      <div className="bg-ink/22 p-4 rounded-control border border-ink-subtle/40 w-full min-w-0">
                        <p
                          className={`text-white leading-relaxed break-words ${
                            embedded
                              ? 'text-ui sm:text-body-l'
                              : 'text-body-xl sm:text-display-s lg:text-display-m font-normal'
                          }`}
                        >
                          {currentData.questions[currentQuestionIndex]}
                        </p>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="flex items-center justify-center gap-1.5 mt-3 shrink-0">
                  {currentData.questions.map((_, index) => (
                    <Pressable
                      key={index}
                      aria-label={`Вопрос ${index + 1}`}
                      aria-current={index === currentQuestionIndex ? 'step' : undefined}
                      onClick={() => setCurrentQuestionIndex(index)}
                      className={`rounded-full transition-all ${
                        index === currentQuestionIndex ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5 opacity-50'
                      }`}
                    >
                      <Circle
                        className={`w-full h-full ${index === currentQuestionIndex ? '' : 'fill-ink-subtle'}`}
                        fill={index === currentQuestionIndex ? currentData.dotColor : undefined}
                      />
                    </Pressable>
                  ))}
                </div>

                <div className="flex gap-2 mt-3 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghostInverse"
                    className="flex-1"
                    startIcon={<ChevronLeft className="w-4 h-4" />}
                    disabled={currentQuestionIndex === 0}
                    onClick={goToPrevQuestion}
                  >
                    Предыдущий
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghostInverse"
                    className="flex-1"
                    endIcon={<ChevronRight className="w-4 h-4" />}
                    disabled={currentQuestionIndex === currentData.questions.length - 1}
                    onClick={goToNextQuestion}
                  >
                    Следующий
                  </Button>
                </div>
              </div>

              <div className="p-2 border-t border-ink-subtle/45 flex gap-2 shrink-0 bg-ink/24">
                <Button
                  type="button"
                  size="sm"
                  variant="ghostInverse"
                  className="flex-1"
                  startIcon={<ChevronLeft className="w-4 h-4" />}
                  disabled={currentIndex === 0}
                  onClick={goToPrevStage}
                >
                  Предыдущий этап
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  className="flex-1"
                  endIcon={<ChevronRight className="w-4 h-4" />}
                  disabled={currentIndex === stageOrder.length - 1}
                  onClick={goToNextStage}
                >
                  Следующий этап
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
